import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import nds from '@nerdwallet/base-styles/classes';
import { Box, Title, Text, Link } from '@nerdwallet/currency';
import Image from '@nerdwallet/react-image';

import {
  stringInputSpec,
  stringTextAreaSpec,
} from '../../../../lib/editing-configs';
import parseStyleFormatting from '../parse-awards-2023-formatting';
import styles from './layout-l1.module.less';
import { useScrollAnimation } from '../useScrollAnimation';

const LayoutL1 = ({
  title,
  subtitle,
  brandLogoUrl,
  brandLogoLink,
  desktopLargeStarUrl,
  desktopSmallStarUrl,
  topRightIconUrl,
  topLeftIconUrlMobile,
}) => {
  const topLeftIconMobileRef = useRef();
  const desktopLargeStarRef = useRef();
  const desktopSmallStarRef = useRef();
  const bigOverflowStarRef = useRef();
  const smallOverflowStarRef = useRef();
  const mobileLeftBigStarRef = useRef();

  useScrollAnimation([
    { elementRef: topLeftIconMobileRef, speed: 5 },
    { elementRef: desktopSmallStarRef, speed: 2 },
    { elementRef: bigOverflowStarRef, speed: 2 },
    { elementRef: desktopLargeStarRef, speed: 2 },
    { elementRef: mobileLeftBigStarRef, speed: 2 },
    { elementRef: smallOverflowStarRef, speed: 5 },
  ]);

  return (
    <Box className={styles.container}>
      <Box className={styles.awardsHero2023BrandLogo}>
        {topLeftIconUrlMobile && (
          <Box
            ref={topLeftIconMobileRef}
            className={styles.awardsHero2023LeftStarsMobile}
          >
            <Image
              naturalHeight={98}
              naturalWidth={98}
              backgroundColor="transparent"
              className={styles.topLeftIconMobile}
              src={topRightIconUrl}
              alt="big star"
            />
          </Box>
        )}
        {brandLogoUrl && (
          <Box className={styles.awardsHero2023LogoWrapper}>
            {brandLogoLink ? (
              <Link href={brandLogoLink}>
                <Image
                  naturalHeight={244}
                  naturalWidth={313}
                  backgroundColor="transparent"
                  src={brandLogoUrl}
                  alt="brand logo"
                />
              </Link>
            ) : (
              <Image
                naturalHeight={244}
                naturalWidth={313}
                backgroundColor="transparent"
                src={brandLogoUrl}
                alt="brand logo"
              />
            )}
          </Box>
        )}
      </Box>

      <Box className={styles.awardsHero2023Main}>
        <Box className={styles.awardsHero2023LeftStarsLarge}>
          {desktopLargeStarUrl && (
            <Box ref={desktopLargeStarRef}>
              <Image
                naturalHeight={149}
                naturalWidth={149}
                backgroundColor="transparent"
                src={desktopLargeStarUrl}
                alt="big star"
              />
            </Box>
          )}
          {desktopSmallStarUrl && (
            <Box
              ref={desktopSmallStarRef}
              className={styles.desktopLeftSmallStar}
            >
              <Image
                naturalHeight={62}
                naturalWidth={62}
                backgroundColor="transparent"
                src={desktopSmallStarUrl}
                alt="small star"
              />
            </Box>
          )}
        </Box>

        <Box className={styles.awardsHero2023TextContainer}>
          {topLeftIconUrlMobile && (
            <Box className={styles.mobileBigLeftStarContainer}>
              <Image
                naturalHeight={101}
                naturalWidth={101}
                backgroundColor="transparent"
                ref={mobileLeftBigStarRef}
                id="yellow-star"
                src={desktopLargeStarUrl}
                alt="yellow star"
              />
            </Box>
          )}
          <Box
            display="flex"
            flexDirection="column"
            className={classNames([nds('margin-vertical-3')])}
          >
            <Title className={styles.awardsHero2023TextTitle}>
              {parseStyleFormatting(title)}
            </Title>
            <Box className={styles.awardsHero2023TextSubtitle}>
              <Text component="p">{subtitle}</Text>
            </Box>
          </Box>
        </Box>
        <Box className={styles.overflowStars}>
          {topRightIconUrl && (
            <Box ref={smallOverflowStarRef} className={styles.smallStar}>
              <Image
                naturalHeight={46}
                naturalWidth={46}
                className="small-star"
                backgroundColor="transparent"
                id="pink-star"
                src={topRightIconUrl}
                alt="pink star"
              />
            </Box>
          )}
          {topLeftIconUrlMobile && (
            <Box ref={bigOverflowStarRef} className={styles.largeStar}>
              <Image
                naturalHeight={150}
                naturalWidth={150}
                className="large-star"
                backgroundColor="transparent"
                id="yellow-star"
                src={topLeftIconUrlMobile}
                alt="yellow star"
              />
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
};

LayoutL1.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  brandLogoUrl: PropTypes.string,
  brandLogoLink: PropTypes.string,
  desktopLargeStarUrl: PropTypes.string,
  desktopSmallStarUrl: PropTypes.string,
  topRightIconUrl: PropTypes.string,
  topLeftIconUrlMobile: PropTypes.string,
};

LayoutL1.defaultProps = {
  brandLogoUrl: '',
  brandLogoLink: '',
};

LayoutL1.getEditingConfig = () => {
  return [
    stringTextAreaSpec('title', 'Title', {
      help: 'For Gotham black font surround with a | like |this|, For italics, surround title text with ^ like ^this^. To include a linebreak, insert :br: in the textbox',
    }),
    stringTextAreaSpec('subtitle', 'Subtitle'),
    stringInputSpec('brandLogoUrl', 'Brand Logo URL'),
    stringInputSpec('brandLogoLink', 'Brand Logo Link'),
    stringInputSpec('desktopLargeStarUrl', 'Desktop Large Star URL'),
    stringInputSpec('desktopSmallStarUrl', 'Desktop Small Star URL'),
    stringInputSpec('topLeftIconUrlMobile', 'Large Overflow Image URL'),
    stringInputSpec('topRightIconUrl', 'Top Right Image URL'),
  ];
};

export default LayoutL1;
